/**
 * Sidebar — section-based navigation inspired by legacy MM portal.
 *
 * Three top-level sections (Company / Events / Tools), each with its own
 * nav group. Active section is driven by current route path. Admin section
 * visible only to admin users.
 */

import { NavLink, useLocation, useNavigate } from 'react-router-dom'
import {
  LayoutDashboard,
  BarChart3,
  GitCompare,
  History,
  Brain,
  Ticket,
  Trophy,
  Lightbulb,
  TrendingUp,
  DollarSign,
  FileText,
  FileUp,
  Settings2,
  LogOut,
  PanelLeftClose,
  PanelLeftOpen,
  BookOpen,
  Building2,
  CalendarDays,
  Wrench,
  Shield,
  Users,
  MessageCircle,
  Activity,
  Bot,
  MapPin,
  type LucideIcon,
} from 'lucide-react'
import { useAuth } from '@/contexts/AuthContext'
import { useOperator } from '@/contexts/OperatorContext'

interface NavItem {
  to: string
  label: string
  icon: LucideIcon
}

interface Section {
  id: string
  label: string
  icon: LucideIcon
  items: NavItem[]
  matchPrefix: string[]
}

const COMPANY: Section = {
  id: 'company',
  label: 'Company',
  icon: Building2,
  matchPrefix: ['/company', '/season'],
  items: [
    { to: '/company', label: 'Dashboard', icon: LayoutDashboard },
    { to: '/company/events', label: 'All Events', icon: BarChart3 },
    { to: '/company/rankings', label: 'Rankings', icon: Trophy },
    { to: '/company/trends', label: 'Trends', icon: TrendingUp },
    { to: '/company/plans', label: 'Plans', icon: Ticket },
    { to: '/company/attractions', label: 'Attractions', icon: Activity },
  ],
}

const EVENTS: Section = {
  id: 'events',
  label: 'Events',
  icon: CalendarDays,
  matchPrefix: ['/home', '/analytics', '/intelligence', '/ride-intelligence', '/history', '/rankings', '/insights', '/deep-analytics', '/revenue', '/guest-groups', '/events', '/map'],
  items: [
    { to: '/home', label: 'Dashboard', icon: LayoutDashboard },
    { to: '/analytics', label: 'Analytics', icon: BarChart3 },
    { to: '/revenue', label: 'Revenue', icon: DollarSign },
    { to: '/guest-groups', label: 'Guest Groups', icon: Users },
    { to: '/analytics/compare', label: 'Compare', icon: GitCompare },
    { to: '/intelligence', label: 'Intelligence', icon: Brain },
    { to: '/ride-intelligence', label: 'Ride Intelligence', icon: Activity },
    { to: '/map', label: 'Layout Map', icon: MapPin },
    { to: '/rankings', label: 'Rankings', icon: Trophy },
    { to: '/insights', label: 'Insights', icon: Lightbulb },
    { to: '/deep-analytics', label: 'Deep Analytics', icon: TrendingUp },
    { to: '/history', label: 'Event History', icon: History },
  ],
}

const TOOLS: Section = {
  id: 'tools',
  label: 'Tools',
  icon: Wrench,
  matchPrefix: ['/reports', '/import', '/help', '/settings', '/landing'],
  items: [
    { to: '/reports', label: 'Reports', icon: FileText },
    { to: '/import', label: 'Smart Import', icon: FileUp },
    { to: '/help', label: 'Help', icon: BookOpen },
    { to: '/settings', label: 'Settings', icon: Settings2 },
  ],
}

const ADMIN: Section = {
  id: 'admin',
  label: 'Admin',
  icon: Shield,
  matchPrefix: ['/admin'],
  items: [
    { to: '/admin/users', label: 'Users', icon: Users },
    { to: '/admin/chat-monitor', label: 'Chat Monitor', icon: MessageCircle },
    { to: '/admin/usage', label: 'Usage', icon: Activity },
    { to: '/admin/agent', label: 'Platform Agent', icon: Bot },
    { to: '/admin/insights', label: 'Insights', icon: Lightbulb },
  ],
}

const ALL_SECTIONS = [COMPANY, EVENTS, TOOLS, ADMIN]

function detectSection(pathname: string): string {
  for (const s of ALL_SECTIONS) {
    if (s.matchPrefix.some(p => pathname === p || pathname.startsWith(p + '/'))) {
      return s.id
    }
  }
  return 'events'
}

interface SidebarProps {
  collapsed?: boolean
  onToggleCollapsed?: () => void
}

function SectionTab({ section, active, collapsed, onClick }: {
  section: Section; active: boolean; collapsed: boolean; onClick: () => void
}) {
  const Icon = section.icon
  return (
    <button
      type="button"
      onClick={onClick}
      title={section.label}
      className={`flex items-center gap-2.5 w-full h-9 text-[12px] font-semibold uppercase tracking-[0.06em] transition-colors ${
        collapsed ? 'px-4 justify-center' : 'px-5'
      } ${
        active
          ? 'text-butler-600 bg-butler-50/60'
          : 'text-slate-400 hover:text-slate-600 hover:bg-slate-50'
      }`}
    >
      <Icon className="w-4 h-4 shrink-0" />
      {!collapsed && <span>{section.label}</span>}
    </button>
  )
}

function NavGroup({ items, collapsed }: { items: NavItem[]; collapsed: boolean }) {
  return (
    <div className="py-1">
      {items.map((item) => (
        <NavLink
          key={item.to}
          to={item.to}
          aria-label={collapsed ? item.label : undefined}
          title={collapsed ? item.label : undefined}
          className={({ isActive }) =>
            `flex items-center gap-3 h-[38px] text-[13px] font-medium transition-all border-l-[3px] ${
              collapsed ? 'px-4 justify-center' : 'px-5'
            } ${
              isActive
                ? 'bg-[rgba(0,112,171,0.07)] border-l-butler-600 text-butler-600 font-semibold'
                : 'border-l-transparent text-slate-500 hover:bg-[rgba(0,112,171,0.04)] hover:text-slate-800'
            }`
          }
        >
          <item.icon className="w-[17px] h-[17px] shrink-0" />
          {!collapsed && <span>{item.label}</span>}
        </NavLink>
      ))}
    </div>
  )
}

export function Sidebar({ collapsed = false, onToggleCollapsed }: SidebarProps) {
  const { logout, claims } = useAuth()
  const { short: brandName, logoUrl, company } = useOperator()
  const location = useLocation()
  const navigate = useNavigate()
  const activeSection = detectSection(location.pathname)
  const isAdmin = claims?.role === 'admin'
  const sections = isAdmin ? ALL_SECTIONS : ALL_SECTIONS.filter(s => s.id !== 'admin')
  const current = sections.find(s => s.id === activeSection) ?? EVENTS

  return (
    <aside
      data-testid="sidebar"
      data-collapsed={collapsed ? 'true' : 'false'}
      className={`${
        collapsed ? 'w-16' : 'w-60'
      } flex-col shrink-0 h-full min-h-0 bg-white border-r border-slate-200 transition-[width] duration-150 overflow-y-auto hidden lg:flex ${
        !collapsed ? 'max-lg:fixed max-lg:inset-y-0 max-lg:left-0 max-lg:z-30 max-lg:flex max-lg:shadow-xl' : ''
      }`}
    >
      {/* Brand lockup */}
      <div className="flex items-center gap-3 px-4 py-3 border-b border-slate-100 min-h-[52px]">
        <img
          src={logoUrl}
          alt={company}
          className="w-8 h-8 rounded object-contain shrink-0"
        />
        {!collapsed && (
          <div className="min-w-0 flex-1">
            <div className="font-bold text-sm leading-tight text-slate-800 truncate"
              style={{ fontFamily: "'Rubik', 'Montserrat', sans-serif" }}
            >
              {brandName}
            </div>
            <div className="text-[11px] text-slate-400 leading-tight truncate">
              {company}
            </div>
          </div>
        )}
        {onToggleCollapsed && (
          <button
            type="button"
            onClick={onToggleCollapsed}
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            className="rounded-md p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
          >
            {collapsed ? (
              <PanelLeftOpen className="w-4 h-4" />
            ) : (
              <PanelLeftClose className="w-4 h-4" />
            )}
          </button>
        )}
      </div>

      {/* Section tabs */}
      <div className="border-b border-slate-100 py-1.5">
        {sections.map(s => (
          <SectionTab
            key={s.id}
            section={s}
            active={s.id === activeSection}
            collapsed={collapsed}
            onClick={() => {
              navigate(s.items[0].to)
            }}
          />
        ))}
      </div>

      {/* Active section nav items */}
      <div className="flex-1 pt-1">
        {!collapsed && (
          <div className="px-5 pt-3 pb-1.5 text-[10px] font-semibold uppercase tracking-[0.5px] text-slate-400">
            {current.label}
          </div>
        )}
        <NavGroup items={current.items} collapsed={collapsed} />
      </div>

      {/* Footer */}
      <div className="border-t border-slate-100">
        <button
          type="button"
          onClick={logout}
          className={`flex items-center gap-3 w-full h-[38px] text-[13px] font-medium text-slate-400 hover:text-red-500 hover:bg-red-50/50 transition-colors ${
            collapsed ? 'px-4 justify-center' : 'px-5'
          }`}
        >
          <LogOut className="w-[17px] h-[17px]" />
          {!collapsed && <span>Sign out</span>}
        </button>
        {!collapsed && (
          <div className="px-5 py-3 text-[11px] text-slate-300 flex items-center gap-1.5">
            Powered by <span className="text-butler-500 font-semibold">MagicMoney</span>
          </div>
        )}
      </div>
    </aside>
  )
}
